import type { AthleteProfile } from "../athlete-profile/types/athleteProfile";
import type { PreferredUnits } from "../athlete-profile/types/athleteProfile";
import type { CompletedActivityRecord } from "../train/completedActivityHistory";
import { formatMinutesAsDuration, formatWeightFromKilograms } from "../../lib/unitPreferences";

export const ATHLETE_CONTEXT_SCHEMA_VERSION = 2;
const CONTEXT_DB_NAME = "johan-performance-lab-athlete-timeline";
const CONTEXT_DB_VERSION = 2;
const CONTEXT_STORE_NAME = "timeline-events";

export type ContextEventType =
  | "ride"
  | "ride_analysis"
  | "workout"
  | "recovery"
  | "sleep"
  | "body_weight"
  | "body_fat"
  | "waist"
  | "bloodwork"
  | "medication"
  | "supplement"
  | "nutrition_block"
  | "race"
  | "equipment_change"
  | "injury"
  | "illness"
  | "travel"
  | "manual_note"
  | "ftp_snapshot"
  | "activity_completed"
  | "ftp_change"
  | "health_import"
  | "body_measurement"
  | "nutrition_block_start"
  | "nutrition_block_end"
  | "supplement_change"
  | "medication_change"
  | "recovery_protocol"
  | "injury_or_symptom"
  | "athlete_note";

export type ContextEventSource =
  | "automatic_completed_activity"
  | "automatic_athlete_profile"
  | "manual_entry"
  | "imported_source"
  | "derived_context";

export type ContextConfidence = "high" | "medium" | "low" | "context_only";

export type ContextProvenance = {
  sourceSystem: string;
  sourceId?: string;
  importSessionId?: string;
  detail: string;
  derivedFromIds: string[];
};

export type ContextEvent = {
  schemaVersion: number;
  id: string;
  timestamp: string;
  type: ContextEventType;
  source: ContextEventSource;
  sourceId?: string;
  confidence: ContextConfidence;
  tags: string[];
  metrics?: Record<string, number>;
  linkedEntityIds: string[];
  provenance: ContextProvenance;
  title: string;
  summary: string;
  notes?: string;
  eventDate: string;
  eventTime?: string;
  canonicalTimestampIso?: string;
  createdAt: string;
  updatedAt: string;
  experimentId?: string;
  interventionId?: string;
  blockId?: string;
};

export type TimelineFilter =
  | "all"
  | "activities"
  | "performance"
  | "nutrition"
  | "recovery"
  | "body_composition"
  | "medical_context"
  | "equipment"
  | "notes";

export type ContextUpsertResult = {
  backend: "indexeddb" | "memory";
  databaseStatus: "unknown" | "opening" | "open" | "failed";
  event: ContextEvent;
  action: "created" | "updated" | "unchanged";
  stableSourceKey: string | null;
  duplicateCandidatesFound: number;
};

export type ContextReconciliationResult = {
  duplicateCandidatesFound: number;
  duplicateEventsReconciled: number;
};

export type UpsertContextEventInput = Omit<ContextEvent, "schemaVersion" | "id" | "createdAt" | "updatedAt" | "linkedEntityIds" | "provenance" | "timestamp"> & {
  id?: string;
  linkedEntityIds?: string[];
  provenance?: Partial<ContextProvenance>;
  timestamp?: string;
};

export type FindEventsNearDateInput = {
  timestamp: string;
  types?: ContextEventType[];
  excludeIds?: string[];
  maxHours?: number;
};

export type GetRelatedContextInput = {
  eventId?: string;
  timestamp?: string;
  types?: ContextEventType[];
  excludeTypes?: ContextEventType[];
  excludeIds?: string[];
  limit?: number;
  maxHours?: number;
  preferredUnits?: PreferredUnits;
};

export type ContextSummaryItem = {
  id: string;
  type: ContextEventType;
  label: string;
  relativeTimeLabel: string;
  timestamp: string;
};

type StoreStatus = "unknown" | "opening" | "open" | "failed";
type OpenContextStoreResult = {
  backend: "indexeddb" | "memory";
  databaseStatus: StoreStatus;
  database: IDBDatabase | null;
};

const DEFAULT_LINK_WINDOWS_HOURS: Partial<Record<ContextEventType, number>> = {
  ride: 8,
  ride_analysis: 12,
  workout: 12,
  recovery: 18,
  sleep: 18,
  body_weight: 12,
  body_fat: 24,
  waist: 24,
  bloodwork: 72,
  medication: 72,
  supplement: 72,
  nutrition_block: 144,
  race: 24,
  equipment_change: 36,
  injury: 168,
  illness: 168,
  travel: 36,
  manual_note: 72,
  ftp_snapshot: 24,
  activity_completed: 12,
  ftp_change: 168,
  health_import: 24,
  body_measurement: 24,
  nutrition_block_start: 144,
  nutrition_block_end: 144,
  supplement_change: 72,
  medication_change: 72,
  recovery_protocol: 18,
  injury_or_symptom: 168,
  athlete_note: 72
};

const memoryContextStore = new Map<string, ContextEvent>();
let contextBackend: "indexeddb" | "memory" = "indexeddb";
let contextDatabaseStatus: StoreStatus = "unknown";
let contextDatabase: IDBDatabase | null = null;
let contextInitialization: Promise<OpenContextStoreResult> | null = null;

function nowIso(): string {
  return new Date().toISOString();
}

function pad2(value: number): string {
  return String(value).padStart(2, "0");
}

function toLocalDateKeyFromMs(timestampMs: number): string {
  const date = new Date(timestampMs);
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

export function toCalendarDateKey(isoTimestamp: string, timeZone?: string): string {
  const parsed = Date.parse(isoTimestamp);
  if (Number.isNaN(parsed)) {
    return toLocalDateKeyFromMs(Date.now());
  }

  if (!timeZone) {
    return toLocalDateKeyFromMs(parsed);
  }

  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit"
  });
  const parts = formatter.formatToParts(new Date(parsed));
  const year = parts.find(part => part.type === "year")?.value;
  const month = parts.find(part => part.type === "month")?.value;
  const day = parts.find(part => part.type === "day")?.value;
  if (!year || !month || !day) {
    return toLocalDateKeyFromMs(parsed);
  }
  return `${year}-${month}-${day}`;
}

function parseDateKeyMs(dateKey: string): number {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateKey.trim());
  if (!match) return 0;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return 0;
  return new Date(year, month - 1, day).getTime();
}

function normalizeDateKey(value: string): string {
  const trimmed = value.trim();
  const dateKeyMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed);
  if (dateKeyMatch) {
    return `${dateKeyMatch[1]}-${dateKeyMatch[2]}-${dateKeyMatch[3]}`;
  }

  const parsed = Date.parse(trimmed);
  if (Number.isNaN(parsed)) {
    return toLocalDateKeyFromMs(Date.now());
  }
  return toLocalDateKeyFromMs(parsed);
}

function normalizeEventType(type: string, metrics?: Record<string, number>): ContextEventType {
  switch (type) {
    case "body_measurement":
      if (typeof metrics?.body_fat_percentage === "number") return "body_fat";
      if (typeof metrics?.waist_cm === "number" || typeof metrics?.waist_circumference_cm === "number") return "waist";
      return "body_measurement";
    case "supplement_change":
      return "supplement";
    case "medication_change":
      return "medication";
    case "recovery_protocol":
      return "recovery";
    case "injury_or_symptom":
      return "injury";
    case "athlete_note":
      return "manual_note";
    default:
      return type as ContextEventType;
  }
}

function normalizeSource(source: unknown): ContextEventSource {
  if (source === "automatic_completed_activity" || source === "automatic_athlete_profile" || source === "manual_entry" || source === "imported_source" || source === "derived_context") {
    return source;
  }
  return "manual_entry";
}

function normalizeConfidence(confidence: unknown): ContextConfidence {
  if (confidence === "high" || confidence === "medium" || confidence === "low" || confidence === "context_only") {
    return confidence;
  }
  return "context_only";
}

function normalizeMetrics(metrics: unknown): Record<string, number> | undefined {
  if (!metrics || typeof metrics !== "object") {
    return undefined;
  }

  const entries = Object.entries(metrics as Record<string, unknown>)
    .filter(([, value]) => typeof value === "number" && Number.isFinite(value));

  if (entries.length === 0) {
    return undefined;
  }

  return Object.fromEntries(entries) as Record<string, number>;
}

function defaultProvenance(source: ContextEventSource, sourceId: string | undefined, notes: string | undefined): ContextProvenance {
  return {
    sourceSystem: source,
    sourceId,
    detail: notes?.trim() || `Provenance: ${source}`,
    derivedFromIds: []
  };
}

function eventSortDesc(left: ContextEvent, right: ContextEvent): number {
  const leftTimestamp = Date.parse(left.timestamp);
  const rightTimestamp = Date.parse(right.timestamp);
  if (!Number.isNaN(leftTimestamp) && !Number.isNaN(rightTimestamp) && leftTimestamp !== rightTimestamp) {
    return rightTimestamp - leftTimestamp;
  }

  const leftDate = parseDateKeyMs(left.eventDate);
  const rightDate = parseDateKeyMs(right.eventDate);
  if (leftDate !== rightDate) return rightDate - leftDate;

  const leftTime = left.eventTime ?? "00:00";
  const rightTime = right.eventTime ?? "00:00";
  if (leftTime !== rightTime) return rightTime.localeCompare(leftTime);

  return right.updatedAt.localeCompare(left.updatedAt);
}

function nextId(): string {
  return `context-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function normalizeTimestamp(input: { timestamp?: string; canonicalTimestampIso?: string; eventDate?: string; eventTime?: string }): string {
  const direct = input.timestamp?.trim() || input.canonicalTimestampIso?.trim();
  if (direct) {
    const parsed = Date.parse(direct);
    if (!Number.isNaN(parsed)) {
      return new Date(parsed).toISOString();
    }
  }

  const date = normalizeDateKey(input.eventDate ?? nowIso());
  const time = (input.eventTime?.trim() || "12:00").slice(0, 5);
  const parsed = Date.parse(`${date}T${time}:00`);
  return Number.isNaN(parsed) ? nowIso() : new Date(parsed).toISOString();
}

function extractTimeLabel(timestamp: string): string | undefined {
  const parsed = Date.parse(timestamp);
  if (Number.isNaN(parsed)) {
    return undefined;
  }
  const date = new Date(parsed);
  return `${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
}

function cloneEvent(event: ContextEvent): ContextEvent {
  return {
    ...event,
    tags: [...event.tags],
    metrics: event.metrics ? { ...event.metrics } : undefined,
    linkedEntityIds: [...event.linkedEntityIds],
    provenance: {
      ...event.provenance,
      derivedFromIds: [...event.provenance.derivedFromIds]
    }
  };
}

function stableEventSignature(event: ContextEvent): string {
  return JSON.stringify({
    timestamp: event.timestamp,
    type: event.type,
    source: event.source,
    sourceId: event.sourceId ?? null,
    confidence: event.confidence,
    tags: [...event.tags].sort(),
    metrics: event.metrics ?? null,
    linkedEntityIds: [...event.linkedEntityIds].sort(),
    provenance: {
      ...event.provenance,
      derivedFromIds: [...event.provenance.derivedFromIds].sort()
    },
    title: event.title,
    summary: event.summary,
    notes: event.notes ?? null,
    eventDate: event.eventDate,
    eventTime: event.eventTime ?? null,
    canonicalTimestampIso: event.canonicalTimestampIso ?? null,
    experimentId: event.experimentId ?? null,
    interventionId: event.interventionId ?? null,
    blockId: event.blockId ?? null
  });
}

function normalizeAutomaticActivitySourceId(event: Pick<ContextEvent, "source" | "type" | "sourceId" | "canonicalTimestampIso" | "metrics" | "tags">): string | null {
  if (event.source !== "automatic_completed_activity") {
    return event.sourceId ?? null;
  }

  const start = event.canonicalTimestampIso ?? "none";
  const duration = event.metrics?.durationSeconds ?? "none";
  const distance = event.metrics?.distanceMeters ?? "none";
  const sourceHashTag = event.tags.find(tag => tag.startsWith("source_hash:"));
  const sourceHash = sourceHashTag ? sourceHashTag.slice("source_hash:".length) : "none";
  const fallback = `${start}:${duration}:${distance}:${sourceHash}`;

  if (event.type === "activity_completed") {
    if (event.sourceId?.startsWith("activity_completed:")) return event.sourceId;
    if (event.sourceId?.trim()) return `activity_completed:${event.sourceId.trim()}`;
    return `activity_completed:fallback:${fallback}`;
  }

  if (event.type === "ride") {
    if (event.sourceId?.startsWith("ride:")) return event.sourceId;
    if (event.sourceId?.trim()) return `ride:${event.sourceId.trim().replace(/^activity_completed:/, "")}`;
    return `ride:fallback:${fallback}`;
  }

  if (event.type === "ride_analysis") {
    if (event.sourceId?.startsWith("ride_analysis:")) return event.sourceId;
    if (event.sourceId?.trim()) return `ride_analysis:${event.sourceId.trim().replace(/^activity_completed:/, "")}`;
    return `ride_analysis:fallback:${fallback}`;
  }

  if (event.type === "ftp_snapshot") {
    if (event.sourceId?.startsWith("ftp_snapshot:")) return event.sourceId;
    if (event.sourceId?.trim()) return `ftp_snapshot:${event.sourceId.trim().replace(/^activity_completed:/, "")}`;
    return `ftp_snapshot:fallback:${fallback}`;
  }

  if (event.type === "race") {
    if (event.sourceId?.startsWith("race:")) return event.sourceId;
    if (event.sourceId?.trim()) return `race:${event.sourceId.trim().replace(/^activity_completed:/, "")}`;
    return `race:fallback:${fallback}`;
  }

  return event.sourceId ?? null;
}

function normalizeBaseSourceId(sourceId: string | undefined): string | null {
  if (!sourceId) return null;
  return sourceId
    .replace(/^race:/, "")
    .replace(/^ride_analysis:/, "")
    .replace(/^ride:/, "")
    .replace(/^ftp_snapshot:/, "")
    .replace(/^activity_completed:/, "")
    .trim() || null;
}

function migrateStoredEvent(rawEvent: unknown): ContextEvent {
  const input = typeof rawEvent === "object" && rawEvent !== null ? rawEvent as Record<string, unknown> : {};
  const metrics = normalizeMetrics(input.metrics);
  const type = normalizeEventType(String(input.type ?? input.eventType ?? "manual_note"), metrics);
  const source = normalizeSource(input.source);
  const timestamp = normalizeTimestamp({
    timestamp: typeof input.timestamp === "string" ? input.timestamp : undefined,
    canonicalTimestampIso: typeof input.canonicalTimestampIso === "string" ? input.canonicalTimestampIso : undefined,
    eventDate: typeof input.eventDate === "string" ? input.eventDate : undefined,
    eventTime: typeof input.eventTime === "string" ? input.eventTime : undefined
  });
  const eventDate = normalizeDateKey(typeof input.eventDate === "string" ? input.eventDate : timestamp);
  const provenanceInput = typeof input.provenance === "object" && input.provenance !== null ? input.provenance as Record<string, unknown> : null;

  return {
    schemaVersion: ATHLETE_CONTEXT_SCHEMA_VERSION,
    id: typeof input.id === "string" && input.id.trim() ? input.id : nextId(),
    timestamp,
    type,
    source,
    sourceId: typeof input.sourceId === "string" && input.sourceId.trim() ? input.sourceId.trim() : undefined,
    confidence: normalizeConfidence(input.confidence),
    tags: Array.isArray(input.tags) ? input.tags.filter((tag): tag is string => typeof tag === "string" && tag.trim().length > 0).map(tag => tag.trim()) : [],
    metrics,
    linkedEntityIds: Array.isArray(input.linkedEntityIds)
      ? input.linkedEntityIds.filter((value): value is string => typeof value === "string" && value.trim().length > 0)
      : [],
    provenance: provenanceInput
      ? {
          sourceSystem: typeof provenanceInput.sourceSystem === "string" && provenanceInput.sourceSystem.trim() ? provenanceInput.sourceSystem : source,
          sourceId: typeof provenanceInput.sourceId === "string" && provenanceInput.sourceId.trim() ? provenanceInput.sourceId : undefined,
          importSessionId: typeof provenanceInput.importSessionId === "string" && provenanceInput.importSessionId.trim() ? provenanceInput.importSessionId : undefined,
          detail: typeof provenanceInput.detail === "string" && provenanceInput.detail.trim() ? provenanceInput.detail : `Provenance: ${source}`,
          derivedFromIds: Array.isArray(provenanceInput.derivedFromIds)
            ? provenanceInput.derivedFromIds.filter((value): value is string => typeof value === "string" && value.trim().length > 0)
            : []
        }
      : defaultProvenance(source, typeof input.sourceId === "string" ? input.sourceId : undefined, typeof input.notes === "string" ? input.notes : undefined),
    title: typeof input.title === "string" ? input.title.trim() : "Context event",
    summary: typeof input.summary === "string" ? input.summary.trim() : "",
    notes: typeof input.notes === "string" && input.notes.trim() ? input.notes : undefined,
    eventDate,
    eventTime: typeof input.eventTime === "string" && input.eventTime.trim() ? input.eventTime : extractTimeLabel(timestamp),
    canonicalTimestampIso: typeof input.canonicalTimestampIso === "string" && input.canonicalTimestampIso.trim() ? input.canonicalTimestampIso : timestamp,
    createdAt: typeof input.createdAt === "string" && input.createdAt.trim() ? input.createdAt : nowIso(),
    updatedAt: typeof input.updatedAt === "string" && input.updatedAt.trim() ? input.updatedAt : nowIso(),
    experimentId: typeof input.experimentId === "string" && input.experimentId.trim() ? input.experimentId : undefined,
    interventionId: typeof input.interventionId === "string" && input.interventionId.trim() ? input.interventionId : undefined,
    blockId: typeof input.blockId === "string" && input.blockId.trim() ? input.blockId : undefined
  };
}

async function initializeContextStore(): Promise<OpenContextStoreResult> {
  if (contextDatabaseStatus === "open" && contextDatabase) {
    return { backend: contextBackend, databaseStatus: contextDatabaseStatus, database: contextDatabase };
  }
  if (contextInitialization) {
    return contextInitialization;
  }

  contextDatabaseStatus = "opening";

  if (typeof indexedDB === "undefined") {
    contextBackend = "memory";
    contextDatabaseStatus = "failed";
    return { backend: contextBackend, databaseStatus: contextDatabaseStatus, database: null };
  }

  contextInitialization = new Promise(resolve => {
    const request = indexedDB.open(CONTEXT_DB_NAME, CONTEXT_DB_VERSION);
    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(CONTEXT_STORE_NAME)) {
        database.createObjectStore(CONTEXT_STORE_NAME, { keyPath: "id" });
      }
    };
    request.onerror = () => {
      contextBackend = "memory";
      contextDatabaseStatus = "failed";
      contextInitialization = null;
      resolve({ backend: contextBackend, databaseStatus: contextDatabaseStatus, database: null });
    };
    request.onblocked = () => {
      contextBackend = "memory";
      contextDatabaseStatus = "failed";
      contextInitialization = null;
      resolve({ backend: contextBackend, databaseStatus: contextDatabaseStatus, database: null });
    };
    request.onsuccess = () => {
      contextBackend = "indexeddb";
      contextDatabaseStatus = "open";
      contextDatabase = request.result;
      contextDatabase.onclose = () => {
        contextDatabaseStatus = "failed";
        contextDatabase = null;
      };
      contextInitialization = null;
      resolve({ backend: contextBackend, databaseStatus: contextDatabaseStatus, database: contextDatabase });
    };
  });

  return contextInitialization;
}

async function listIndexedDbContextEvents(database: IDBDatabase): Promise<ContextEvent[]> {
  return new Promise(resolve => {
    const transaction = database.transaction(CONTEXT_STORE_NAME, "readonly");
    const store = transaction.objectStore(CONTEXT_STORE_NAME);
    const request = store.getAll();
    request.onerror = () => resolve([]);
    request.onsuccess = () => {
      const events = ((request.result as unknown[] | undefined) ?? []).map(migrateStoredEvent);
      resolve(events);
    };
  });
}

async function putIndexedDbContextEvent(database: IDBDatabase, event: ContextEvent): Promise<void> {
  await new Promise<void>((resolve, reject) => {
    const transaction = database.transaction(CONTEXT_STORE_NAME, "readwrite");
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error ?? new Error("Unable to save context event."));
    transaction.objectStore(CONTEXT_STORE_NAME).put(event);
  });
}

async function deleteIndexedDbContextEvent(database: IDBDatabase, id: string): Promise<void> {
  await new Promise<void>(resolve => {
    const transaction = database.transaction(CONTEXT_STORE_NAME, "readwrite");
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => resolve();
    transaction.objectStore(CONTEXT_STORE_NAME).delete(id);
  });
}

async function listAllEvents(opened: OpenContextStoreResult): Promise<ContextEvent[]> {
  if (opened.database) {
    return listIndexedDbContextEvents(opened.database);
  }
  return [...memoryContextStore.values()].map(cloneEvent);
}

async function persistEvent(opened: OpenContextStoreResult, event: ContextEvent): Promise<void> {
  if (opened.database) {
    await putIndexedDbContextEvent(opened.database, event);
    return;
  }
  memoryContextStore.set(event.id, cloneEvent(event));
}

async function removeEvent(opened: OpenContextStoreResult, id: string): Promise<void> {
  if (opened.database) {
    await deleteIndexedDbContextEvent(opened.database, id);
    return;
  }
  memoryContextStore.delete(id);
}

function normalizeUpsertInput(input: UpsertContextEventInput): UpsertContextEventInput {
  const normalizedTimestamp = normalizeTimestamp({
    timestamp: input.timestamp,
    canonicalTimestampIso: input.canonicalTimestampIso,
    eventDate: input.eventDate,
    eventTime: input.eventTime
  });
  return {
    ...input,
    type: normalizeEventType(input.type, input.metrics),
    eventDate: normalizeDateKey(input.eventDate || normalizedTimestamp),
    eventTime: input.eventTime?.trim() || extractTimeLabel(normalizedTimestamp),
    timestamp: normalizedTimestamp,
    title: input.title.trim(),
    summary: input.summary.trim(),
    sourceId: input.sourceId?.trim() || undefined,
    canonicalTimestampIso: input.canonicalTimestampIso?.trim() || normalizedTimestamp,
    tags: input.tags.map(tag => tag.trim()).filter(Boolean),
    metrics: normalizeMetrics(input.metrics),
    linkedEntityIds: (input.linkedEntityIds ?? []).map(id => id.trim()).filter(Boolean),
    provenance: input.provenance
  };
}

function contextLinkWindowHours(type: ContextEventType, maxHours?: number): number {
  if (typeof maxHours === "number" && Number.isFinite(maxHours) && maxHours > 0) {
    return maxHours;
  }
  return DEFAULT_LINK_WINDOWS_HOURS[type] ?? 24;
}

function canLinkBySharedIdentity(left: ContextEvent, right: ContextEvent): boolean {
  const leftSource = normalizeBaseSourceId(normalizeAutomaticActivitySourceId(left) ?? left.sourceId);
  const rightSource = normalizeBaseSourceId(normalizeAutomaticActivitySourceId(right) ?? right.sourceId);
  if (leftSource && rightSource && leftSource === rightSource) {
    return true;
  }

  const leftHash = left.tags.find(tag => tag.startsWith("source_hash:"));
  const rightHash = right.tags.find(tag => tag.startsWith("source_hash:"));
  return Boolean(leftHash && rightHash && leftHash === rightHash);
}

function pairShouldLink(left: ContextEvent, right: ContextEvent, maxHours?: number): boolean {
  if (left.id === right.id) return false;
  if (canLinkBySharedIdentity(left, right)) return true;

  const leftTimestamp = Date.parse(left.timestamp);
  const rightTimestamp = Date.parse(right.timestamp);
  if (Number.isNaN(leftTimestamp) || Number.isNaN(rightTimestamp)) {
    return left.eventDate === right.eventDate;
  }

  const hoursApart = Math.abs(leftTimestamp - rightTimestamp) / (1000 * 60 * 60);
  return hoursApart <= Math.max(contextLinkWindowHours(left.type, maxHours), contextLinkWindowHours(right.type, maxHours));
}

export function linkContextEvents(events: ContextEvent[], options?: { maxHours?: number }): ContextEvent[] {
  const cloned = events.map(cloneEvent);
  const linkedIds = new Map<string, Set<string>>();
  for (const event of cloned) {
    linkedIds.set(event.id, new Set(event.linkedEntityIds));
  }

  for (let index = 0; index < cloned.length; index += 1) {
    for (let compareIndex = index + 1; compareIndex < cloned.length; compareIndex += 1) {
      const left = cloned[index];
      const right = cloned[compareIndex];
      if (!pairShouldLink(left, right, options?.maxHours)) {
        continue;
      }
      linkedIds.get(left.id)?.add(right.id);
      linkedIds.get(right.id)?.add(left.id);
    }
  }

  return cloned.map(event => ({
    ...event,
    linkedEntityIds: [...(linkedIds.get(event.id) ?? new Set<string>())].sort()
  }));
}

async function synchronizeContextGraph(opened: OpenContextStoreResult): Promise<ContextEvent[]> {
  const existing = await listAllEvents(opened);
  const linked = linkContextEvents(existing);

  await Promise.all(linked.map(async event => {
    const prior = existing.find(candidate => candidate.id === event.id);
    if (!prior || stableEventSignature(prior) !== stableEventSignature(event) || prior.schemaVersion !== ATHLETE_CONTEXT_SCHEMA_VERSION) {
      await persistEvent(opened, event);
    }
  }));

  return linked.sort(eventSortDesc);
}

export async function listAthleteContextEvents(): Promise<ContextEvent[]> {
  const opened = await initializeContextStore();
  return synchronizeContextGraph(opened);
}

export async function upsertAthleteContextEvent(input: UpsertContextEventInput): Promise<ContextUpsertResult> {
  const opened = await initializeContextStore();
  const all = await listAllEvents(opened);
  const normalized = normalizeUpsertInput(input);
  const now = nowIso();

  const sourceForNormalization: ContextEvent = migrateStoredEvent({
    source: normalized.source,
    type: normalized.type,
    sourceId: normalized.sourceId,
    canonicalTimestampIso: normalized.canonicalTimestampIso,
    metrics: normalized.metrics,
    tags: normalized.tags,
    title: normalized.title,
    summary: normalized.summary,
    eventDate: normalized.eventDate,
    eventTime: normalized.eventTime,
    confidence: normalized.confidence
  });
  const normalizedSourceId = normalizeAutomaticActivitySourceId(sourceForNormalization);

  const existingBySource = normalizedSourceId
    ? all.find(event => {
        const candidateSourceId = normalizeAutomaticActivitySourceId(event);
        return event.source === normalized.source && event.type === normalized.type && candidateSourceId === normalizedSourceId;
      })
    : null;
  const existingById = normalized.id ? all.find(event => event.id === normalized.id) : null;
  const existing = existingById ?? existingBySource;

  const nextEvent: ContextEvent = {
    schemaVersion: ATHLETE_CONTEXT_SCHEMA_VERSION,
    id: existing?.id ?? normalized.id ?? nextId(),
    timestamp: normalized.timestamp ?? now,
    type: normalized.type,
    source: normalized.source,
    sourceId: normalizedSourceId ?? normalized.sourceId,
    confidence: normalized.confidence,
    tags: normalized.tags,
    metrics: normalized.metrics,
    linkedEntityIds: existing?.linkedEntityIds ?? normalized.linkedEntityIds ?? [],
    provenance: {
      ...defaultProvenance(normalized.source, normalizedSourceId ?? normalized.sourceId, normalized.notes),
      ...(normalized.provenance ?? {}),
      derivedFromIds: [...(normalized.provenance?.derivedFromIds ?? existing?.provenance.derivedFromIds ?? [])]
    },
    title: normalized.title,
    summary: normalized.summary,
    notes: normalized.notes,
    eventDate: normalized.eventDate,
    eventTime: normalized.eventTime,
    canonicalTimestampIso: normalized.canonicalTimestampIso,
    createdAt: existing?.createdAt ?? now,
    updatedAt: now,
    experimentId: normalized.experimentId,
    interventionId: normalized.interventionId,
    blockId: normalized.blockId
  };

  if (existing) {
    if (stableEventSignature(existing) === stableEventSignature(nextEvent)) {
      return {
        backend: opened.backend,
        databaseStatus: opened.databaseStatus,
        event: existing,
        action: "unchanged",
        stableSourceKey: normalizedSourceId,
        duplicateCandidatesFound: 0
      };
    }
  }

  await persistEvent(opened, nextEvent);
  const relinked = await synchronizeContextGraph(opened);
  const persisted = relinked.find(event => event.id === nextEvent.id) ?? nextEvent;

  return {
    backend: opened.backend,
    databaseStatus: opened.databaseStatus,
    event: persisted,
    action: existing ? "updated" : "created",
    stableSourceKey: normalizedSourceId,
    duplicateCandidatesFound: existing ? 1 : 0
  };
}

export async function reconcileAthleteContextDuplicateAutomaticEvents(): Promise<ContextReconciliationResult> {
  const opened = await initializeContextStore();
  const all = await listAllEvents(opened);

  const duplicateCandidates = new Map<string, ContextEvent[]>();
  for (const event of all) {
    if (event.source !== "automatic_completed_activity") continue;

    const duration = event.metrics?.durationSeconds ?? "none";
    const distance = event.metrics?.distanceMeters ?? "none";
    const sourceHashTag = event.tags.find(tag => tag.startsWith("source_hash:"));
    const sourceHash = sourceHashTag ? sourceHashTag.slice("source_hash:".length) : "none";
    const fingerprint = `${event.type}:${event.canonicalTimestampIso ?? "none"}:${duration}:${distance}:${sourceHash}`;
    const bucket = duplicateCandidates.get(fingerprint) ?? [];
    bucket.push(event);
    duplicateCandidates.set(fingerprint, bucket);
  }

  let duplicateEventsReconciled = 0;
  let duplicateCandidatesFound = 0;

  for (const [groupKey, group] of duplicateCandidates) {
    if (group.length <= 1) {
      const only = group[0];
      const normalizedOnlyKey = only ? normalizeAutomaticActivitySourceId(only) : null;
      if (only && normalizedOnlyKey && only.sourceId !== normalizedOnlyKey) {
        await persistEvent(opened, { ...only, sourceId: normalizedOnlyKey, updatedAt: nowIso() });
      }
      continue;
    }

    duplicateCandidatesFound += group.length;
    const sortedGroup = [...group].sort((left, right) => {
      const updatedOrder = right.updatedAt.localeCompare(left.updatedAt);
      if (updatedOrder !== 0) return updatedOrder;

      const createdOrder = right.createdAt.localeCompare(left.createdAt);
      if (createdOrder !== 0) return createdOrder;

      return right.id.localeCompare(left.id);
    });
    const winner = sortedGroup[0];
    const canonicalSourceKey = normalizeAutomaticActivitySourceId(winner) ?? winner.sourceId ?? groupKey;
    const normalizedWinner = winner.sourceId === canonicalSourceKey
      ? winner
      : { ...winner, sourceId: canonicalSourceKey, updatedAt: nowIso() };
    await persistEvent(opened, normalizedWinner);

    for (const duplicate of sortedGroup.slice(1)) {
      await removeEvent(opened, duplicate.id);
      duplicateEventsReconciled += 1;
    }
  }

  await synchronizeContextGraph(opened);

  return {
    duplicateCandidatesFound,
    duplicateEventsReconciled
  };
}

export async function deleteAthleteContextEvent(id: string): Promise<void> {
  const opened = await initializeContextStore();
  await removeEvent(opened, id);
  await synchronizeContextGraph(opened);
}

export function findEventsNearDate(events: ContextEvent[], input: FindEventsNearDateInput): ContextEvent[] {
  const anchorTimestamp = Date.parse(input.timestamp);
  const excluded = new Set(input.excludeIds ?? []);
  return events
    .filter(event => !excluded.has(event.id))
    .filter(event => !input.types || input.types.includes(event.type))
    .filter(event => {
      if (Number.isNaN(anchorTimestamp)) {
        return event.eventDate === normalizeDateKey(input.timestamp);
      }
      const eventTimestamp = Date.parse(event.timestamp);
      if (Number.isNaN(eventTimestamp)) {
        return event.eventDate === toCalendarDateKey(input.timestamp);
      }
      const hoursApart = Math.abs(eventTimestamp - anchorTimestamp) / (1000 * 60 * 60);
      const maxHours = input.maxHours ?? contextLinkWindowHours(event.type);
      return hoursApart <= maxHours;
    })
    .sort((left, right) => {
      const leftDelta = Math.abs(Date.parse(left.timestamp) - anchorTimestamp);
      const rightDelta = Math.abs(Date.parse(right.timestamp) - anchorTimestamp);
      if (leftDelta !== rightDelta) return leftDelta - rightDelta;
      return eventSortDesc(left, right);
    });
}

export function getRelatedContext(events: ContextEvent[], input: GetRelatedContextInput): { anchorEvent: ContextEvent | null; relatedEvents: ContextEvent[] } {
  const anchorEvent = input.eventId ? events.find(event => event.id === input.eventId) ?? null : null;
  const anchorTimestamp = input.timestamp ?? anchorEvent?.timestamp ?? null;
  if (!anchorTimestamp) {
    return { anchorEvent, relatedEvents: [] };
  }

  const excludedIds = new Set(input.excludeIds ?? []);
  if (anchorEvent) {
    excludedIds.add(anchorEvent.id);
  }

  const directlyLinked = anchorEvent
    ? anchorEvent.linkedEntityIds
        .map(id => events.find(event => event.id === id) ?? null)
        .filter((event): event is ContextEvent => event !== null)
    : [];
  const nearby = findEventsNearDate(events, {
    timestamp: anchorTimestamp,
    types: input.types,
    excludeIds: [...excludedIds],
    maxHours: input.maxHours
  });

  const combined = [...directlyLinked, ...nearby]
    .filter(event => !excludedIds.has(event.id))
    .filter(event => !(input.excludeTypes?.includes(event.type) ?? false));

  const deduped = new Map<string, ContextEvent>();
  for (const event of combined) {
    deduped.set(event.id, event);
  }

  const relatedEvents = [...deduped.values()]
    .sort((left, right) => {
      const leftLinked = anchorEvent?.linkedEntityIds.includes(left.id) ? 0 : 1;
      const rightLinked = anchorEvent?.linkedEntityIds.includes(right.id) ? 0 : 1;
      if (leftLinked !== rightLinked) return leftLinked - rightLinked;

      const leftDelta = Math.abs(Date.parse(left.timestamp) - Date.parse(anchorTimestamp));
      const rightDelta = Math.abs(Date.parse(right.timestamp) - Date.parse(anchorTimestamp));
      if (leftDelta !== rightDelta) return leftDelta - rightDelta;
      return eventSortDesc(left, right);
    })
    .slice(0, input.limit ?? 8);

  return { anchorEvent, relatedEvents };
}

function formatDurationMinutes(totalMinutes: number | undefined): string | null {
  if (typeof totalMinutes !== "number" || !Number.isFinite(totalMinutes) || totalMinutes <= 0) {
    return null;
  }
  const hours = Math.floor(totalMinutes / 60);
  const minutes = Math.round(totalMinutes % 60);
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
}

function relativeTimeLabel(anchorTimestamp: string, eventTimestamp: string): string {
  const anchor = Date.parse(anchorTimestamp);
  const event = Date.parse(eventTimestamp);
  if (Number.isNaN(anchor) || Number.isNaN(event)) {
    return "nearby";
  }

  const deltaHours = Math.abs(anchor - event) / (1000 * 60 * 60);
  if (deltaHours < 1) {
    return event <= anchor ? "earlier" : "later";
  }
  if (deltaHours < 24) {
    const rounded = Math.round(deltaHours);
    return event <= anchor ? `${rounded} hour${rounded === 1 ? "" : "s"} earlier` : `${rounded} hour${rounded === 1 ? "" : "s"} later`;
  }
  const days = Math.round(deltaHours / 24);
  return event <= anchor ? `${days} day${days === 1 ? "" : "s"} earlier` : `${days} day${days === 1 ? "" : "s"} later`;
}

function relativeTimeFromRideLabel(anchorTimestamp: string, eventTimestamp: string): string {
  const anchor = Date.parse(anchorTimestamp);
  const event = Date.parse(eventTimestamp);
  if (Number.isNaN(anchor) || Number.isNaN(event)) {
    return "near ride";
  }

  const diffMs = event - anchor;
  const diffMinutes = Math.round(Math.abs(diffMs) / (1000 * 60));
  if (diffMinutes <= 1) {
    return "during ride";
  }

  if (diffMinutes < 60) {
    return `${diffMinutes} minute${diffMinutes === 1 ? "" : "s"} ${diffMs < 0 ? "before" : "after"} ride`;
  }

  const diffHours = Math.round(diffMinutes / 60);
  if (diffHours < 24) {
    return `${diffHours} hour${diffHours === 1 ? "" : "s"} ${diffMs < 0 ? "before" : "after"} ride`;
  }

  const diffDays = Math.round(diffHours / 24);
  return `${diffDays} day${diffDays === 1 ? "" : "s"} ${diffMs < 0 ? "before" : "after"} ride`;
}

function joinContextLabel(parts: Array<string | null | undefined>): string {
  return parts.filter((part): part is string => Boolean(part && part.trim())).join(" • ");
}

function formatSummaryLabel(event: ContextEvent, anchorTimestamp: string, preferredUnits: PreferredUnits): string {
  const delta = relativeTimeFromRideLabel(anchorTimestamp, event.timestamp);
  switch (normalizeEventType(event.type, event.metrics)) {
    case "body_weight": {
      const weightKg = event.metrics?.weight_kg;
      const weightLb = event.metrics?.weight_lb;
      const value = typeof weightKg === "number"
        ? formatWeightFromKilograms(weightKg, preferredUnits)
        : typeof weightLb === "number"
          ? formatWeightFromKilograms(weightLb * 0.45359237, preferredUnits)
          : null;
      return joinContextLabel(["Body weight recorded", value, delta]);
    }
    case "sleep": {
      const duration = typeof event.metrics?.totalSleepMinutes === "number" ? formatMinutesAsDuration(event.metrics.totalSleepMinutes) : null;
      return joinContextLabel(["Sleep recorded", duration, delta]);
    }
    case "recovery": {
      const hrv = event.metrics?.hrvSdnnMs;
      const rhr = event.metrics?.restingHeartRateBpm;
      const detail = typeof hrv === "number" && typeof rhr === "number"
        ? `HRV ${Math.round(hrv)} ms, RHR ${Math.round(rhr)} bpm`
        : typeof hrv === "number"
          ? `HRV ${Math.round(hrv)} ms`
          : null;
      return joinContextLabel(["Recovery metrics recorded", detail, delta]);
    }
    case "body_fat":
      return joinContextLabel([
        "Body fat recorded",
        typeof event.metrics?.body_fat_percentage === "number" ? `${event.metrics.body_fat_percentage.toFixed(1)}%` : null,
        delta
      ]);
    case "waist":
      return joinContextLabel([
        "Waist recorded",
        typeof event.metrics?.waist_cm === "number" ? `${event.metrics.waist_cm.toFixed(1)} cm` : null,
        delta
      ]);
    case "medication":
    case "supplement":
    case "nutrition_block":
    case "equipment_change":
    case "injury":
    case "illness":
    case "travel":
    case "manual_note":
    case "race":
      return joinContextLabel([event.title, delta]);
    case "ftp_snapshot":
      return joinContextLabel([
        "FTP snapshot",
        typeof event.metrics?.ftpSnapshot === "number" ? `${Math.round(event.metrics.ftpSnapshot)} W` : event.title,
        delta
      ]);
    case "ride":
    case "ride_analysis":
    case "activity_completed":
      return joinContextLabel([event.title, delta]);
    default:
      return joinContextLabel([event.title || event.summary, delta]);
  }
}

export function getContextSummary(events: ContextEvent[], input: GetRelatedContextInput): ContextSummaryItem[] {
  const related = getRelatedContext(events, input);
  const anchorTimestamp = input.timestamp ?? related.anchorEvent?.timestamp;
  if (!anchorTimestamp) {
    return [];
  }

  return related.relatedEvents.map(event => ({
    id: event.id,
    type: event.type,
    label: formatSummaryLabel(event, anchorTimestamp, input.preferredUnits ?? "imperial"),
    relativeTimeLabel: relativeTimeLabel(anchorTimestamp, event.timestamp),
    timestamp: event.timestamp
  }));
}

export function buildAutomaticActivityStableSourceKey(record: CompletedActivityRecord): string {
  if (record.activityId && record.activityId.trim().length > 0) {
    return `activity_completed:${record.activityId.trim()}`;
  }

  const start = record.duplicateIdentity.startTimestampMs ?? "none";
  const duration = record.duplicateIdentity.durationSeconds ?? "none";
  const distance = record.duplicateIdentity.distanceMeters ?? "none";
  const sourceHash = record.duplicateIdentity.stableSourceHash || "none";
  return `activity_completed:fallback:${start}:${duration}:${distance}:${sourceHash}`;
}

export function createRideContextEvent(record: CompletedActivityRecord, options?: { timeZone?: string }): UpsertContextEventInput {
  const canonicalTimestampIso = record.activityDate;
  const stableSourceKey = buildAutomaticActivityStableSourceKey(record);
  return {
    eventDate: toCalendarDateKey(canonicalTimestampIso, options?.timeZone),
    canonicalTimestampIso,
    timestamp: canonicalTimestampIso,
    type: "ride",
    title: record.activityTitle || record.rideClassification.displayName,
    summary: `${record.activityType} recorded. ${record.durationSeconds ? `${Math.round(record.durationSeconds / 60)} min` : "Duration unavailable"}${record.distanceMeters ? ` · ${(record.distanceMeters / 1000).toFixed(1)} km` : ""}.`,
    source: "automatic_completed_activity",
    sourceId: `ride:${stableSourceKey}`,
    tags: [record.rideClassification.type, "activity", `source_hash:${record.duplicateIdentity.stableSourceHash}`],
    metrics: {
      durationSeconds: record.durationSeconds ?? 0,
      distanceMeters: record.distanceMeters ?? 0,
      movingTimeSeconds: record.movingTimeSeconds ?? 0
    },
    confidence: "high",
    notes: `Provenance: automatic ride context for ${record.activityId}`,
    provenance: {
      sourceSystem: "completed_activity_history",
      sourceId: record.activityId,
      detail: `Derived from completed activity ${record.activityId}`,
      derivedFromIds: []
    }
  };
}

export function createActivityCompletedTimelineEvent(record: CompletedActivityRecord, options?: { timeZone?: string }): UpsertContextEventInput {
  const canonicalTimestampIso = record.activityDate;
  const stableSourceKey = buildAutomaticActivityStableSourceKey(record);

  return {
    eventDate: toCalendarDateKey(canonicalTimestampIso, options?.timeZone),
    canonicalTimestampIso,
    timestamp: canonicalTimestampIso,
    type: "activity_completed",
    title: `${record.rideClassification.displayName} completed`,
    summary: `Analysis complete. IF ${record.intensityFactor?.toFixed(2) ?? "--"}, NP ${record.normalizedPower ? `${Math.round(record.normalizedPower)} W` : "--"}, FTP snapshot ${record.ftpSnapshot.ftpWatts ?? "--"} W.`,
    source: "automatic_completed_activity",
    sourceId: stableSourceKey,
    tags: [record.rideClassification.type, "activity", `source_hash:${record.duplicateIdentity.stableSourceHash}`],
    metrics: {
      durationSeconds: record.durationSeconds ?? 0,
      distanceMeters: record.distanceMeters ?? 0,
      intensityFactor: record.intensityFactor ?? 0,
      normalizedPower: record.normalizedPower ?? 0,
      ftpSnapshot: record.ftpSnapshot.ftpWatts ?? 0
    },
    confidence: record.analysisConfidence.overallPercent >= 80 ? "high" : record.analysisConfidence.overallPercent >= 60 ? "medium" : "low",
    notes: `Provenance: automatic from completed activity ${record.activityId}`,
    provenance: {
      sourceSystem: "completed_activity_history",
      sourceId: record.activityId,
      detail: `Analysis snapshot for completed activity ${record.activityId}`,
      derivedFromIds: []
    }
  };
}

export function createRideAnalysisContextEvent(record: CompletedActivityRecord, options?: { timeZone?: string }): UpsertContextEventInput {
  const canonicalTimestampIso = record.activityDate;
  const stableSourceKey = buildAutomaticActivityStableSourceKey(record);
  return {
    eventDate: toCalendarDateKey(canonicalTimestampIso, options?.timeZone),
    canonicalTimestampIso,
    timestamp: canonicalTimestampIso,
    type: "ride_analysis",
    title: `Ride analysis ready for ${record.activityTitle || record.rideClassification.displayName}`,
    summary: `Completed ride analysis captured at ${record.analysisConfidence.overallPercent}% confidence.`,
    source: "automatic_completed_activity",
    sourceId: `ride_analysis:${stableSourceKey}`,
    tags: [record.rideClassification.type, "analysis", `source_hash:${record.duplicateIdentity.stableSourceHash}`],
    metrics: {
      analysisConfidence: record.analysisConfidence.overallPercent,
      intensityFactor: record.intensityFactor ?? 0,
      normalizedPower: record.normalizedPower ?? 0
    },
    confidence: record.analysisConfidence.overallPercent >= 80 ? "high" : record.analysisConfidence.overallPercent >= 60 ? "medium" : "low",
    notes: `Provenance: ride analysis for completed activity ${record.activityId}`,
    provenance: {
      sourceSystem: "completed_activity_history",
      sourceId: record.activityId,
      detail: `Ride analysis event for completed activity ${record.activityId}`,
      derivedFromIds: []
    }
  };
}

export function createFtpSnapshotContextEvent(record: CompletedActivityRecord, options?: { timeZone?: string }): UpsertContextEventInput {
  const canonicalTimestampIso = record.activityDate;
  const stableSourceKey = buildAutomaticActivityStableSourceKey(record);
  return {
    eventDate: toCalendarDateKey(canonicalTimestampIso, options?.timeZone),
    canonicalTimestampIso,
    timestamp: canonicalTimestampIso,
    type: "ftp_snapshot",
    title: `FTP snapshot ${record.ftpSnapshot.ftpWatts ?? "--"} W`,
    summary: `Analysis used ${record.ftpSnapshot.ftpWatts ?? "--"} W from ${record.ftpSnapshot.sourceLabel ?? "unknown source"}.`,
    source: "automatic_completed_activity",
    sourceId: `ftp_snapshot:${stableSourceKey}:${record.ftpSnapshot.ftpWatts ?? "none"}`,
    tags: ["ftp", "performance", `source_hash:${record.duplicateIdentity.stableSourceHash}`],
    metrics: {
      ftpSnapshot: record.ftpSnapshot.ftpWatts ?? 0
    },
    confidence: "high",
    notes: `Provenance: FTP snapshot used by completed activity ${record.activityId}`,
    provenance: {
      sourceSystem: "completed_activity_history",
      sourceId: record.activityId,
      detail: `FTP snapshot attached to completed activity ${record.activityId}`,
      derivedFromIds: []
    }
  };
}

export function createRaceTimelineEvent(record: CompletedActivityRecord): UpsertContextEventInput {
  const baseKey = buildAutomaticActivityStableSourceKey(record);
  return {
    eventDate: toCalendarDateKey(record.activityDate),
    canonicalTimestampIso: record.activityDate,
    timestamp: record.activityDate,
    type: "race",
    title: "Race completed",
    summary: `${record.rideClassification.displayName} classified ride completed with analysis confidence ${record.analysisConfidence.overallPercent}%.`,
    source: "automatic_completed_activity",
    sourceId: `race:${baseKey}`,
    tags: ["race", "activity", `source_hash:${record.duplicateIdentity.stableSourceHash}`],
    metrics: {
      durationSeconds: record.durationSeconds ?? 0,
      distanceMeters: record.distanceMeters ?? 0
    },
    confidence: record.analysisConfidence.rideClassificationPercent >= 80 ? "high" : "medium",
    notes: `Provenance: automatic from completed activity ${record.activityId}`,
    provenance: {
      sourceSystem: "completed_activity_history",
      sourceId: record.activityId,
      detail: `Race classification derived from completed activity ${record.activityId}`,
      derivedFromIds: []
    }
  };
}

export function createFtpChangeTimelineEvent(input: {
  profile: AthleteProfile;
  previousFtpWatts: number;
  nextFtpWatts: number;
}): UpsertContextEventInput {
  const effectiveTimestamp = input.profile.performance.ftpEffectiveDateIso ?? nowIso();
  return {
    eventDate: toCalendarDateKey(effectiveTimestamp),
    canonicalTimestampIso: effectiveTimestamp,
    timestamp: effectiveTimestamp,
    type: "ftp_change",
    title: `FTP changed to ${input.nextFtpWatts} W`,
    summary: `FTP updated from ${input.previousFtpWatts} W to ${input.nextFtpWatts} W in athlete profile.`,
    source: "automatic_athlete_profile",
    sourceId: `ftp:${effectiveTimestamp}:${input.nextFtpWatts}`,
    tags: ["ftp", "performance"],
    metrics: {
      previousFtpWatts: input.previousFtpWatts,
      nextFtpWatts: input.nextFtpWatts
    },
    confidence: "high",
    notes: "Provenance: automatic from athlete profile",
    provenance: {
      sourceSystem: "athlete_profile",
      sourceId: input.profile.personal.id,
      detail: "Athlete profile FTP change",
      derivedFromIds: []
    }
  };
}

function filterTypeMatches(filter: TimelineFilter, type: ContextEventType): boolean {
  if (filter === "all") return true;
  if (filter === "activities") {
    return type === "ride" || type === "ride_analysis" || type === "activity_completed" || type === "workout" || type === "race";
  }
  if (filter === "performance") {
    return type === "ftp_change" || type === "ftp_snapshot" || type === "activity_completed" || type === "ride_analysis";
  }
  if (filter === "nutrition") {
    return type === "nutrition_block" || type === "nutrition_block_start" || type === "nutrition_block_end" || type === "supplement" || type === "supplement_change";
  }
  if (filter === "recovery") {
    return type === "recovery" || type === "sleep" || type === "recovery_protocol" || type === "health_import" || type === "illness";
  }
  if (filter === "body_composition") {
    return type === "body_weight" || type === "body_fat" || type === "waist" || type === "body_measurement";
  }
  if (filter === "medical_context") {
    return type === "medication" || type === "medication_change" || type === "bloodwork" || type === "injury" || type === "injury_or_symptom" || type === "illness";
  }
  if (filter === "equipment") {
    return type === "equipment_change";
  }
  if (filter === "notes") {
    return type === "manual_note" || type === "athlete_note" || type === "travel";
  }
  return true;
}

function runtimeEventType(event: ContextEvent): ContextEventType {
  const legacyEvent = event as ContextEvent & { eventType?: ContextEventType };
  return normalizeEventType(legacyEvent.type ?? legacyEvent.eventType ?? "manual_note", legacyEvent.metrics);
}

export function filterTimelineEvents(events: ContextEvent[], filter: TimelineFilter): ContextEvent[] {
  return events.filter(event => filterTypeMatches(filter, runtimeEventType(event)));
}

export const TIMELINE_FILTERS: TimelineFilter[] = [
  "all",
  "activities",
  "performance",
  "nutrition",
  "recovery",
  "body_composition",
  "medical_context",
  "equipment",
  "notes"
];

export function timelineFilterLabel(filter: TimelineFilter): string {
  switch (filter) {
    case "all":
      return "All";
    case "activities":
      return "Activities";
    case "performance":
      return "Performance";
    case "nutrition":
      return "Nutrition";
    case "recovery":
      return "Recovery";
    case "body_composition":
      return "Body Composition";
    case "medical_context":
      return "Medical Context";
    case "equipment":
      return "Equipment";
    case "notes":
      return "Notes";
    default:
      return "All";
  }
}

export function displayTimelineProvenance(source: ContextEventSource): string {
  if (source === "automatic_completed_activity") return "automatic from completed activity";
  if (source === "automatic_athlete_profile") return "automatic from athlete profile";
  if (source === "manual_entry") return "manual entry";
  if (source === "derived_context") return "derived context";
  return "imported source";
}
